import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, LabelList,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import RiskScoreInfoModal from '../../../shared/components/common/RiskScoreInfoModal';
import ModalCloseButton from '../../../shared/components/modals/ModalCloseButton';
import ContextHelpButton from '../../../shared/components/common/ContextHelpButton';
import { formatCurrencyDynamic } from '../../../utils/numberUtils';
import { formatDate } from '../../../utils/dateUtils';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import AnalyticsContextBanner from './AnalyticsContextBanner';
import './AdminAnalytics.css';

const COLORS = ['var(--dp-accent-primary)', 'var(--dp-accent-secondary)', '#f39c12', '#e74c3c', '#3498db', '#27ae60'];

/**
 * Format cost value dynamically based on amount
 * @param {number} amount - The cost amount to format
 * @returns {string} Formatted cost string with appropriate unit (Cr, L, or number)
 */
const formatCost = (amount) => {
  if (amount >= 10000000) {
    return (amount / 10000000).toFixed(2) + ' Cr';
  } else if (amount >= 100000) {
    return (amount / 100000).toFixed(2) + ' L';
  } else {
    return amount.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }
};

/**
 * Format time metric value intelligently based on duration
 * @param {number} days - Time value in days
 * @returns {string} Formatted time string with appropriate unit (h or d)
 */
const formatMetricTime = (days) => {
  if (days < 1) {
    return (days * 24).toFixed(1) + 'h';
  }
  return days.toFixed(1) + 'd';
};

/** Matches backend ACTIVE_UI_STATUSES / device reliability engine */
const ACTIVE_COMPLAINT_FILTER_STATUS = 'open,acknowledged,in_progress,escalated_to_vendor';

/** Human-readable device age for ranking tables (e.g. "3 years, 45 days"). */
const formatDeviceAgeText = (years, days) => {
  const y = Math.max(0, Number(years) || 0);
  const d = Math.max(0, Number(days) || 0);
  if (y === 0 && d === 0) return 'Under 1 year';
  if (y === 0) return d === 1 ? '1 day' : `${d} days`;
  const yearPart = y === 1 ? '1 year' : `${y} years`;
  if (d === 0) return yearPart;
  const dayPart = d === 1 ? '1 day' : `${d} days`;
  return `${yearPart}, ${dayPart}`;
};

/** Avg restore duration for ranking tables (12-month window). */
const formatRestoreDurationText = (device) => {
  if (device.avg_restore_hours == null) return null;
  const totalDays = Number(device.avg_resolution_days ?? (device.avg_restore_hours / 24));
  if (Number.isNaN(totalDays) || totalDays <= 0) return null;
  if (totalDays < 1) {
    const hours = Math.max(1, Math.round(totalDays * 24));
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  const wholeDays = Math.floor(totalDays);
  const hours = Math.round((totalDays - wholeDays) * 24);
  const dayLabel = wholeDays === 1 ? '1 day' : `${wholeDays} days`;
  if (hours === 0) return dayLabel;
  const hourLabel = hours === 1 ? '1 hour' : `${hours} hours`;
  return `${dayLabel}, ${hourLabel}`;
};

const formatFleetMetricDays = (metric) => {
  if (!metric || !metric.count) {
    return 'N/A';
  }
  return formatMetricTime(metric.avg_days || 0);
};

const ACTIVE_COMPLAINT_STATUS_LABEL = 'open, acknowledged, in progress, or escalated to vendor';

/** Compact critical / active complaint counts for ranking tables. */
const ActiveComplaintsCell = ({ criticalActive, activeCount, total12Mo }) => {
  const critical = criticalActive ?? 0;
  const active = activeCount ?? 0;
  const criticalColor = critical > 0 ? '#e74c3c' : active > 0 ? '#f39c12' : '#27ae60';

  return (
    <div className="analytics-complaints-compact">
      <span className="analytics-metric-primary" style={{ color: criticalColor }}>{critical}</span>
      <span className="analytics-metric-secondary"> / {active}</span>
      {total12Mo != null && (
        <span className="analytics-metric-hint"> ({total12Mo} in 12 mo)</span>
      )}
    </div>
  );
};

/** Age column: duration with icon + installation date. */
const DeviceAgeCell = ({ years, days, installationDate }) => {
  const ageText = formatDeviceAgeText(years, days);
  const installedLabel = installationDate ? formatDate(installationDate) : null;

  return (
    <div className="analytics-age-cell">
      <span className="analytics-age-primary" title={ageText}>
        <span className="analytics-age-emoji" aria-hidden="true">⏱️</span>
        {ageText}
      </span>
      {installedLabel && (
        <span className="analytics-age-date" title={`Installed ${installedLabel}`}>
          <span className="analytics-age-emoji" aria-hidden="true">📅</span>
          {installedLabel}
        </span>
      )}
    </div>
  );
};

/** Restore column: formatted avg restore time (12-month window). */
const RestoreTimeCell = ({ device }) => {
  const duration = formatRestoreDurationText(device);
  if (!duration) {
    return <span className="analytics-restore-cell analytics-restore-cell--na">N/A</span>;
  }
  return (
    <span className="analytics-restore-cell" title="Average restore time (last 12 months)">
      <span className="analytics-restore-emoji" aria-hidden="true">⏱️</span>
      {duration}
    </span>
  );
};

const RankingComplaintsFootnote = ({ show12Mo }) => (
  <p className="analytics-ranking-footnote">
    <strong>Complaints column:</strong> critical active / total active ({ACTIVE_COMPLAINT_STATUS_LABEL}).
    {show12Mo && (
      <> Parenthetical value is <strong>total complaints in the last 12 months</strong> (all severities, includes closed).</>
    )}
  </p>
);

function Analytics({ token, onNavigateToDevicesWithFilter, onNavigateToComplaintsWithFilter, onNavigateToDeviceView, user }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [deviceAvailability, setDeviceAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showRiskScoreInfo, setShowRiskScoreInfo] = useState(false);
  const [reliabilitySortConfig, setReliabilitySortConfig] = useState({ key: 'reliability_score', direction: 'desc' });
  const [riskSortConfig, setRiskSortConfig] = useState({ key: 'risk_score', direction: 'desc' });
  const [costDeptSortConfig, setCostDeptSortConfig] = useState({ key: 'total_cost', direction: 'desc' });
  const [availDeptSortConfig, setAvailDeptSortConfig] = useState({ key: 'avgAvailability', direction: 'asc' });
  const [highValueSortConfig, setHighValueSortConfig] = useState({ key: 'product_cost', direction: 'desc' });
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  // Handle body scroll when modal opens/closes
  useEffect(() => {
    if (selectedDepartment) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedDepartment]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Check if user can see financial data
  // Only superadmin, admin, and manager can see financial data
  // Coordinator, Technician, custodian, and regular users cannot see financial data
  const canSeeFinancialData = user && ['superadmin', 'admin', 'manager'].includes(user.role);
  const [expandedSections, setExpandedSections] = useState({
    executiveSummary: true,
    deviceHealth: true,
    maintenance: true,
    financial: true,
    complaints: true,
    departmentPerformance: true,
    highValueAssets: true
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Define analytics section access by role
  // This is the single source of truth for which sections each role can access
  const ANALYTICS_SECTION_ACCESS = {
    superadmin: 'all',                                                                      // Full access to all sections
    admin: 'all',                                                                           // Full access to all sections
    manager: 'all',                                                                         // Full access to all sections
    coordinator: ['deviceHealth', 'maintenance', 'complaints', 'departmentPerformance'],    // Department operations only
    custodian: ['deviceHealth', 'maintenance', 'complaints', 'departmentPerformance']       // Device inventory & compliance only
    // Note: Technician has separate TechnicianAnalytics.js page
    // Note: central_help_desk has NO access to analytics module (MODULE_ACCESS confirms)
  };

  const allSections = [
    { key: 'executiveSummary', icon: '🎯', title: 'Executive Summary - Key Metrics' },
    { key: 'deviceHealth', icon: '📈', title: 'Device Health' },
    { key: 'maintenance', icon: '🔧', title: 'Maintenance' },
    { key: 'financial', icon: '💰', title: 'Financial', requiresFinancial: true },
    { key: 'complaints', icon: '🚨', title: 'Complaints' },
    { key: 'departmentPerformance', icon: '🏥', title: 'Departments' },
    { key: 'highValueAssets', icon: '💎', title: 'High-Value Assets', requiresFinancial: true }
  ];

  const sections = allSections.filter(section => {
    const userRole = user?.role;
    const allowedSections = ANALYTICS_SECTION_ACCESS[userRole];

    // If user role is not in the access map, deny access
    if (!allowedSections) {
      return false;
    }

    // If role has 'all' access, check financial restrictions
    if (allowedSections === 'all') {
      if (section.requiresFinancial && !canSeeFinancialData) {
        return false;
      }
      return true;
    }

    // For restricted roles, only show sections in their allowed list
    return allowedSections.includes(section.key);
  });

  const scrollToSection = (sectionKey) => {
    const element = document.getElementById(`section-${sectionKey}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Sorting handler for Device Reliability Ranking table
  const handleReliabilitySort = (key) => {
    let direction = 'desc';
    if (reliabilitySortConfig.key === key && reliabilitySortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setReliabilitySortConfig({ key, direction });
  };

  // Sorting handler for Device Risk Assessment table
  const handleRiskSort = (key) => {
    let direction = 'desc';
    if (riskSortConfig.key === key && riskSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setRiskSortConfig({ key, direction });
  };

  const handleCostDeptSort = (key) => {
    let direction = 'desc';
    if (costDeptSortConfig.key === key && costDeptSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setCostDeptSortConfig({ key, direction });
  };

  const handleAvailDeptSort = (key) => {
    let direction = 'desc';
    if (availDeptSortConfig.key === key && availDeptSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setAvailDeptSortConfig({ key, direction });
  };

  const handleHighValueSort = (key) => {
    let direction = 'desc';
    if (highValueSortConfig.key === key && highValueSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setHighValueSortConfig({ key, direction });
  };

  // Helper function to sort array based on config
  const getSortedData = (data, sortConfig) => {
    if (!data || !sortConfig) return data;

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  };

  // Helper to render sort indicator
  const renderSortIndicator = (columnKey, currentSortConfig) => {
    const icon = currentSortConfig.key !== columnKey
      ? '⇅'
      : (currentSortConfig.direction === 'asc' ? '▲' : '▼');
    return <span className="th-sort-icon" aria-hidden="true">{icon}</span>;
  };

  // Get department name from user object
  const getDepartmentName = () => {
    if (user?.departments && user.departments.length > 0) {
      return user.departments[0].name;
    }
    return null;
  };

  const departmentName = getDepartmentName();
  const isDepartmentScoped = user && ['coordinator', 'custodian', 'manager', 'technician'].includes(user.role);

  useEffect(() => {
    fetchAnalytics();
    fetchDeviceAvailability();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const [overviewResponse, maintenanceResponse] = await Promise.all([
        axios.get(API_ENDPOINTS.ANALYTICS.OVERVIEW, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(API_ENDPOINTS.ANALYTICS.MAINTENANCE_REPORT, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setAnalyticsData(overviewResponse.data);
      setMaintenanceData(maintenanceResponse.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load analytics data';
      setError(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceAvailability = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEVICES.LIST + '?per_page=1000', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const devices = response.data.devices || [];
      const availabilityPromises = devices.map(device =>
        axios.get(API_ENDPOINTS.DEVICES.RELIABILITY_METRICS(device.id), {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => ({
          ...device,
          availability_percentage: res.data.availability_percentage || 0,
          avg_resolution_hours: res.data.avg_resolution_hours || 0,
          mtbf_days: res.data.mtbf_days || 0,
          active_complaints: 0 // Will be populated from analytics data
        })).catch(() => ({
          ...device,
          availability_percentage: 0,
          avg_resolution_hours: 0,
          mtbf_days: 0,
          active_complaints: 0
        }))
      );
      const devicesWithAvailability = await Promise.all(availabilityPromises);
      const sorted = devicesWithAvailability.sort((a, b) => a.availability_percentage - b.availability_percentage);
      setDeviceAvailability(sorted);
    } catch (err) {
      console.error('Error fetching device availability:', err);
    }
  };

  if (loading) {
    return <div className="analytics-container"><div className="loading-state">Loading analytics...</div></div>;
  }

  if (error) {
    return <div className="analytics-container"><div className="error-state">{error}</div></div>;
  }

  const SectionHeader = ({ icon, title }) => (
    <div className="admin-analytics-section-header">
      <span className="admin-analytics-section-icon" aria-hidden="true">{icon}</span>
      <h2>{title}</h2>
    </div>
  );

  return (
    <div className="analytics-container">
      {/* Analytics Header */}
      <div className="analytics-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>💡 Analytics & Insights</h1>
          <button onClick={fetchAnalytics} className="btn-refresh" title="Refresh analytics" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p>Explore comprehensive analytics across device health, maintenance compliance, financial metrics, and department performance. Make data-driven decisions to improve operations.</p>
      </div>
      
      {/* Quick Navigation Bar - Horizontal scroll on small screens (keep same UX) */}
      <div
        className={`analytics-admin-quick-nav${isCompactQuickNav ? ' analytics-admin-quick-nav--compact' : ''}`}
      >
        <div className="analytics-admin-quick-nav-label">
          📍 Quick Navigation
        </div>

        <div className="analytics-admin-quick-nav-scroll">
          <div
            className="analytics-admin-quick-nav-links"
            style={{
              flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap'
            }}
          >
            {sections.map((section) => (
              <a
                key={section.key}
                href={`#section-${section.key}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(section.key);
                }}
                title={`Jump to ${section.title}`}
                className="analytics-admin-quick-nav-link"
                onDragStart={(e) => e.preventDefault()}
              >
                <span style={{ fontSize: '1rem', userSelect: 'none', pointerEvents: 'none' }}>
                  {section.icon}
                </span>
                <span style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  {section.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* SECTION 1: EXECUTIVE SUMMARY - KEY METRICS */}
      {sections.some(s => s.key === 'executiveSummary') && (
      <div className="analytics-section-wrapper" id="section-executiveSummary">
        <SectionHeader icon="🎯" title="Executive Summary - Key Metrics" sectionKey="executiveSummary" />
        <div className="section-content">
          {/* COMPACT ALERTS GRID - 2 COLUMNS */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {/* MAINTENANCE READINESS - COMBINED */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: 'var(--dp-accent-primary)', textTransform: 'uppercase', letterSpacing: '0.4px', paddingLeft: '0.5rem' }}>
                🔧 Maintenance
              </div>
              <div className="insight-card-clean warning clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({ ppmStatus: ['NotSet'] })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>No PPM Schedule</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Needs Assignment</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.no_ppm_schedule || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
              <div className="insight-card-clean critical clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                  ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Overdue PPM</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Requires Action</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.overdue_maintenance || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
            </div>

            {/* REGULATORY COMPLIANCE - COMBINED */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#27ae60', textTransform: 'uppercase', letterSpacing: '0.4px', paddingLeft: '0.5rem' }}>
                ✅ Compliance
              </div>
              <div className="insight-card-clean critical clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                  calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Overdue Calibrations</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Urgent</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.calibration_stats?.overdue || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
              <div className="insight-card-clean warning clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                  complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Overdue Compliance</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Requires Review</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.compliance_stats?.overdue || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
            </div>

            {/* COVERAGE & INSURANCE - COMBINED */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#f39c12', textTransform: 'uppercase', letterSpacing: '0.4px', paddingLeft: '0.5rem' }}>
                🛡️ Coverage
              </div>
              <div className="insight-card-clean critical clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                  coverageStatus: ['NotCovered']
               })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>No Coverage</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Unprotected</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.warranty_contract_status?.no_coverage || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
              <div className="insight-card-clean warning clickable" onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                  coverageStatus: ['Expired (0-30d)', 'Expired (30+d)']
               })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Expired Coverage</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Warranty & Contract</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{(analyticsData?.warranty_contract_status?.warranty_expired || 0) + (analyticsData?.warranty_contract_status?.contract_expired || 0)}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
            </div>

            {/* ISSUES & INCIDENTS - COMBINED */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#e74c3c', textTransform: 'uppercase', letterSpacing: '0.4px', paddingLeft: '0.5rem' }}>
                🚨 Issues
              </div>
              <div className="insight-card-clean critical clickable" onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ severity: 'critical', status: ACTIVE_COMPLAINT_FILTER_STATUS })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Critical Complaints</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Immediate</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.critical_complaints_count || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
              <div className="insight-card-clean info clickable" onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ status: ACTIVE_COMPLAINT_FILTER_STATUS })}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>All Active Complaints</h3>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Pending</div>
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{analyticsData?.active_complaints || 0}</div>
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', fontWeight: '600', fontStyle: 'normal', marginTop: '0.5rem' }}>↗ Click to view</div>
              </div>
            </div>

            {/* PERFORMANCE METRICS */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#3498db', textTransform: 'uppercase', letterSpacing: '0.4px', paddingLeft: '0.5rem', marginBottom: '0.75rem' }}>
                📊 Performance
              </div>
              <div className="performance-metrics-grid">
                <div className="insight-card-clean success">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Avg Resolution Time</h3>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>All Resolutions</div>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{formatMetricTime(analyticsData?.avg_resolution_time || 0)}</div>
                  </div>
                </div>
                <div className="insight-card-clean success">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Acknowledgement TAT</h3>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Report to ack (12 mo)</div>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{formatFleetMetricDays(analyticsData?.complaint_metrics?.ack_tat)}</div>
                  </div>
                </div>
                <div className="insight-card-clean success">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Closure TAT</h3>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Report to close (12 mo)</div>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{formatFleetMetricDays(analyticsData?.complaint_metrics?.closure_tat)}</div>
                  </div>
                </div>
                <div className="insight-card-clean info">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>Avg restore (12 mo)</h3>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Impacting, breakdown to close</div>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{formatFleetMetricDays(analyticsData?.complaint_metrics?.mttr)}</div>
                  </div>
                </div>
                <div className="insight-card-clean warning">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#2c3e50' }}>MTBF (12 mo)</h3>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.2rem' }}>Between impacting failures</div>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#2c3e50', minWidth: '50px', textAlign: 'right' }}>{formatFleetMetricDays(analyticsData?.complaint_metrics?.mtbf)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* SECTION 2: DEVICE HEALTH & PERFORMANCE */}
      <div className="analytics-section-wrapper" id="section-deviceHealth">
        <SectionHeader icon="📈" title="Device Health & Performance" sectionKey="deviceHealth" />
        {analyticsData?.device_utilization && (
        <div className="section-content">
            <AnalyticsContextBanner variant="window" title="12-month availability window">
              Device availability uses a rolling <strong>last 12 months</strong> window per device (same engine as Device View). Department breakdown below reflects those percentages.
            </AnalyticsContextBanner>
            <div className="availability-overview-grid">
              <div className="insight-card-clean success">
                <div className="card-header-clean"><span className="card-icon-clean">✅</span><h3>Utilization Rate</h3></div>
                <div className="card-value-clean">{analyticsData.device_utilization.utilization_rate}%</div>
                <div className="card-label-clean">Complaint-Free Devices</div>
              </div>
              <div className="insight-card-clean info">
                <div className="card-header-clean"><span className="card-icon-clean">📋</span><h3>Total Devices</h3></div>
                <div className="card-value-clean">{analyticsData?.total_devices || 0}</div>
                <div className="card-label-clean">Across all departments</div>
              </div>
              <div className="insight-card-clean warning">
                <div className="card-header-clean"><span className="card-icon-clean">⚠️</span><h3>Under Maintenance</h3></div>
                <div className="card-value-clean">{analyticsData.device_utilization.devices_with_active_complaints}</div>
                <div className="card-label-clean">Active issues</div>
              </div>
              {canSeeFinancialData && (
              <div className="insight-card-clean financial">
                <div className="card-header-clean"><span className="card-icon-clean">💰</span><h3>Total Asset Value</h3></div>
                <div className="card-value-clean financial">₹{formatCost((analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_device_cost || 0), 0))}</div>
                <div className="card-label-clean">Device investment</div>
              </div>
              )}
            </div>

            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Device Status Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={[
                      { status: 'No Issues', count: analyticsData.device_utilization.devices_complaint_free },
                      { status: 'Active Issues', count: analyticsData.device_utilization.devices_with_active_complaints },
                      { status: 'Resolved Issues', count: analyticsData.device_utilization.devices_with_any_complaints - analyticsData.device_utilization.devices_with_active_complaints }
                    ]} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={100} label={({ count }) => `${count}`}>
                      <Cell fill="#27ae60" />
                      <Cell fill="#e74c3c" />
                      <Cell fill="#f39c12" />
                    </Pie>
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Device Age Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData?.device_ages || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="age_range" />
                    <YAxis />
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Bar dataKey="count" fill="var(--dp-accent-primary)" name="Devices" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Device Reliability Ranking */}
            {analyticsData?.device_reliability && analyticsData.device_reliability.length > 0 && (
              <div className="chart-card-clean full-width analytics-ranking-card">
                <AnalyticsContextBanner variant="engine" title="Same logic as Device View" compact>
                  Rankings use the device reliability engine: 12-month window, impacting availability, and unified reliability score.
                </AnalyticsContextBanner>
                <h4>📊 Device Reliability Ranking (Top 10)</h4>
                <div className="high-value-table-container analytics-ranking-table-container">
                  <table className="high-value-table analytics-ranking-table">
                    <colgroup>
                      <col className="col-device" />
                      <col className="col-device-id" />
                      <col className="col-age" />
                      <col className="col-complaints" />
                      <col className="col-restore" />
                      <col className="col-score" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="sortable" onClick={() => handleReliabilitySort('name')} title="Sort by device name">Device Name{renderSortIndicator('name', reliabilitySortConfig)}</th>
                        <th title="Equipment identifier">Device ID</th>
                        <th className="center sortable" onClick={() => handleReliabilitySort('age_years')} title="Sort by age">Age{renderSortIndicator('age_years', reliabilitySortConfig)}</th>
                        <th className="center sortable" onClick={() => handleReliabilitySort('active_complaints')}>Complaints{renderSortIndicator('active_complaints', reliabilitySortConfig)}</th>
                        <th className="center sortable" onClick={() => handleReliabilitySort('avg_resolution_days')} title="Avg restore time (12 mo)">Restore{renderSortIndicator('avg_resolution_days', reliabilitySortConfig)}</th>
                        <th className="center sortable col-score" onClick={() => handleReliabilitySort('reliability_score')} title="Reliability score">Score{renderSortIndicator('reliability_score', reliabilitySortConfig)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedData(analyticsData.device_reliability, reliabilitySortConfig).slice(0, 10).map((device, index) => {
                        const scoreColor = device.reliability_score >= 80 ? '#27ae60' : device.reliability_score >= 60 ? '#f39c12' : '#e74c3c';
                        return (
                          <tr
                            key={index}
                            className="clickable-row"
                            onClick={() => onNavigateToDeviceView && onNavigateToDeviceView(device.id)}
                          >
                            <td className="device-name-col">
                              <span className="analytics-ranking-device-name" title={device.name}>{device.name}</span>
                            </td>
                            <td className="col-device-id">
                              <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                            </td>
                            <td className="center col-age">
                              <DeviceAgeCell
                                years={device.age_years}
                                days={device.age_days}
                                installationDate={device.installation_date}
                              />
                            </td>
                            <td className="center col-narrow">
                              <ActiveComplaintsCell
                                criticalActive={device.active_critical_complaints}
                                activeCount={device.active_complaints}
                                total12Mo={device.total_complaints}
                              />
                            </td>
                            <td className="center col-narrow">
                              <RestoreTimeCell device={device} />
                            </td>
                            <td className="center col-score">
                              <span className="analytics-score-pill" style={{ color: scoreColor, backgroundColor: `${scoreColor}20` }}>{device.reliability_score.toFixed(1)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <RankingComplaintsFootnote show12Mo />
              </div>
            )}
            {/* Device Risk Score */}
            {analyticsData?.device_risk_scores && analyticsData.device_risk_scores.length > 0 && (
              <div className="chart-card-clean full-width analytics-ranking-card analytics-ranking-card--follow">
                <AnalyticsContextBanner variant="risk" title="How risk scores are calculated" compact>
                  Risk scores match Device View (maintenance, complaint severity, device age).
                </AnalyticsContextBanner>
                <div className="analytics-ranking-header">
                  <h4>⚠️ Device Risk Assessment (Top 10)</h4>
                  <ContextHelpButton
                    onClick={() => setShowRiskScoreInfo(!showRiskScoreInfo)}
                    title="Click to see how Risk Score is calculated"
                  />
                </div>
                <div className="high-value-table-container analytics-ranking-table-container">
                  <table className="high-value-table analytics-ranking-table analytics-ranking-table--risk">
                    <colgroup>
                      <col className="col-device" />
                      <col className="col-device-id" />
                      <col className="col-age" />
                      <col className="col-complaints" />
                      <col className="col-flag" />
                      <col className="col-flag" />
                      <col className="col-flag" />
                      <col className="col-score" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="sortable" onClick={() => handleRiskSort('name')} title="Sort by device name">Device Name{renderSortIndicator('name', riskSortConfig)}</th>
                        <th title="Equipment identifier">Device ID</th>
                        <th className="center sortable" onClick={() => handleRiskSort('age_years')} title="Sort by age">Age{renderSortIndicator('age_years', riskSortConfig)}</th>
                        <th className="center sortable" onClick={() => handleRiskSort('active_complaints')}>Complaints{renderSortIndicator('active_complaints', riskSortConfig)}</th>
                        <th className="center sortable col-flag" onClick={() => handleRiskSort('ppm_overdue')} title="PPM overdue">PPM{renderSortIndicator('ppm_overdue', riskSortConfig)}</th>
                        <th className="center sortable col-flag" onClick={() => handleRiskSort('calibration_overdue')} title="Calibration overdue">Cal{renderSortIndicator('calibration_overdue', riskSortConfig)}</th>
                        <th className="center sortable col-flag" onClick={() => handleRiskSort('warranty_expired')} title="Warranty expired">Warr.{renderSortIndicator('warranty_expired', riskSortConfig)}</th>
                        <th className="center sortable col-score" onClick={() => handleRiskSort('risk_score')} title="Risk score">Risk{renderSortIndicator('risk_score', riskSortConfig)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedData(analyticsData.device_risk_scores.filter(d => d.risk_score >= 3), riskSortConfig).slice(0, 10).map((device, index) => {
                        const riskColor = device.risk_score >= 7 ? '#e74c3c' : device.risk_score >= 5 ? '#f39c12' : '#27ae60';
                        const rowClass = device.risk_score >= 8 ? 'risk-critical' : '';
                        const flagClass = (isOverdue) => (isOverdue ? 'analytics-flag analytics-flag--bad' : 'analytics-flag analytics-flag--ok');
                        return (
                          <tr
                            key={index}
                            className={`clickable-row ${rowClass}`}
                            onClick={() => onNavigateToDeviceView && onNavigateToDeviceView(device.id)}
                          >
                            <td className="device-name-col">
                              <span className="analytics-ranking-device-name" title={device.name}>{device.name}</span>
                            </td>
                            <td className="col-device-id">
                              <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                            </td>
                            <td className="center col-age">
                              <DeviceAgeCell
                                years={device.age_years}
                                days={device.age_days}
                                installationDate={device.installation_date}
                              />
                            </td>
                            <td className="center col-narrow">
                              <ActiveComplaintsCell
                                criticalActive={device.active_critical_complaints}
                                activeCount={device.active_complaints}
                              />
                            </td>
                            <td className="center col-flag"><span className={flagClass(device.ppm_overdue)}>{device.ppm_overdue ? '✗' : '✓'}</span></td>
                            <td className="center col-flag"><span className={flagClass(device.calibration_overdue)}>{device.calibration_overdue ? '✗' : '✓'}</span></td>
                            <td className="center col-flag"><span className={flagClass(device.warranty_expired)}>{device.warranty_expired ? '✗' : '✓'}</span></td>
                            <td className="center col-score">
                              <span className="analytics-score-pill" style={{ color: riskColor, backgroundColor: `${riskColor}20` }}>{device.risk_score}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <RankingComplaintsFootnote />
              </div>
            )}

            {/* Devices Nearing End-of-Life */}
            {analyticsData?.devices_end_of_life && analyticsData.devices_end_of_life.length > 0 && (
              <div className="chart-card-clean full-width">
                <h4>🔴 Devices Nearing End-of-Life (7+ Years Old)</h4>
                <div className="high-value-table-container">
                  <table className="high-value-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #f3f4f6 100%)', borderBottom: '2px solid #e5e7eb' }}>
                      <tr>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }}>Device Name</th>
                        <th style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }}>Age (Years)</th>
                        <th style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }}>Total Complaints</th>
                        <th style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }}>Active Issues</th>
                        <th style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }}>Device Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.devices_end_of_life.map((device, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: device.age_years >= 10 ? 'rgba(231, 76, 60, 0.05)' : 'rgba(243, 156, 18, 0.05)' }}>
                          <td style={{ padding: '1rem', fontWeight: 600, color: '#2c3e50' }}>{device.name}</td>
                          <td style={{ padding: '1rem', textAlign: 'center', color: device.age_years >= 10 ? '#e74c3c' : '#f39c12', fontWeight: 700 }}>{device.age_years}</td>
                          <td style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50' }}>{device.total_complaints}</td>
                          <td style={{ padding: '1rem', textAlign: 'center', color: device.active_complaints > 0 ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>{device.active_complaints}</td>
                          <td style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontFamily: "'Courier New', monospace" }}>₹{device.product_cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* SECTION 3: MAINTENANCE & COMPLIANCE (MERGED with WARRANTY) */}
      <div className="analytics-section-wrapper" id="section-maintenance">
        <SectionHeader icon="🔧" title="Maintenance & Compliance" sectionKey="maintenance" />
        <div className="section-content">
            <div className="key-insights-grid">
              <div className="insight-card-clean success">
                <div className="card-header-clean"><span className="card-icon-clean">📋</span><h3>PPM Compliant</h3></div>
                <div className="card-value-clean">{analyticsData?.ppm_compliance?.compliant || 0}</div>
                <div className="card-label-clean">Devices with scheduled PPM</div>
              </div>
              <div className="insight-card-clean critical">
                <div className="card-header-clean"><span className="card-icon-clean">🔴</span><h3>PPM Overdue</h3></div>
                <div className="card-value-clean">{analyticsData?.ppm_compliance?.overdue || 0}</div>
                <div className="card-label-clean">Requires immediate action</div>
              </div>
              <div className="insight-card-clean info">
                <div className="card-header-clean"><span className="card-icon-clean">📏</span><h3>Calibration Due</h3></div>
                <div className="card-value-clean">{analyticsData?.calibration_stats?.due_soon || 0}</div>
                <div className="card-label-clean">Within 30 days</div>
              </div>
              <div className="insight-card-clean warning">
                <div className="card-header-clean"><span className="card-icon-clean">⏰</span><h3>Calibration Overdue</h3></div>
                <div className="card-value-clean">{analyticsData?.calibration_stats?.overdue || 0}</div>
                <div className="card-label-clean">Urgent attention needed</div>
              </div>
            </div>

            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Maintenance Trends (Last 6 Months)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData?.maintenance_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="var(--dp-accent-primary)" strokeWidth={3} name="Maintenance Activities" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>PPM Compliance Status</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={[
                      { name: 'Compliant', value: analyticsData?.ppm_compliance?.compliant || 0 },
                      { name: 'Overdue', value: analyticsData?.ppm_compliance?.overdue || 0 },
                      { name: 'No Schedule', value: analyticsData?.ppm_compliance?.no_schedule || 0 }
                    ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                      <Cell fill="#27ae60" />
                      <Cell fill="#e74c3c" />
                      <Cell fill="#95a5a6" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Calibration Compliance Status</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={[
                      { name: 'Compliant', value: analyticsData?.calibration_stats?.compliant || 0 },
                      { name: 'Due Soon', value: analyticsData?.calibration_stats?.due_soon || 0 },
                      { name: 'Overdue', value: analyticsData?.calibration_stats?.overdue || 0 }
                    ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                      <Cell fill="#27ae60" />
                      <Cell fill="#f39c12" />
                      <Cell fill="#e74c3c" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Warranty Expiry Timeline</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={(analyticsData?.warranty_timeline || []).filter(item => item.count > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="timeframe" angle={-15} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="count" fill="#e74c3c" name="Devices" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Contract Expiry Timeline</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={(analyticsData?.contract_timeline || []).filter(item => item.count > 0)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="timeframe" angle={-15} textAnchor="end" height={60} />
                    <YAxis />
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="count" fill="#f39c12" name="Devices" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
        </div>
      </div>

      {/* SECTION 4: FINANCIAL ANALYSIS */}
      {canSeeFinancialData && (
      <div className="analytics-section-wrapper" id="section-financial">
        <SectionHeader icon="💰" title="Financial Analysis" sectionKey="financial" />
        <div className="section-content">
            <div className="key-insights-grid">
              <div className="insight-card-clean financial">
                <div className="card-header-clean"><span className="card-icon-clean">💵</span><h3>Total Device Cost</h3></div>
                <div className="card-value-clean financial">₹{formatCost((analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_device_cost || 0), 0))}</div>
                <div className="card-label-clean">One-time investment</div>
              </div>
              <div className="insight-card-clean financial">
                <div className="card-header-clean"><span className="card-icon-clean">📋</span><h3>Total Contract Value</h3></div>
                <div className="card-value-clean financial">₹{formatCost((analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_contract_cost || 0), 0))}</div>
                <div className="card-label-clean">Full contract period</div>
              </div>
              <div className="insight-card-clean financial">
                <div className="card-header-clean"><span className="card-icon-clean">📈</span><h3>Annual Contract Cost</h3></div>
                <div className="card-value-clean financial">₹{formatCost(((analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_contract_cost || 0), 0)) / 5)}</div>
                <div className="card-label-clean">Avg per year (YoY)</div>
              </div>
              <div className="insight-card-clean financial">
                <div className="card-header-clean"><span className="card-icon-clean">💰🖥️</span><h3>Avg Cost per Device</h3></div>
                <div className="card-value-clean financial">₹{formatCost(((analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_device_cost || 0), 0)) / (analyticsData?.total_devices || 1))}</div>
                <div className="card-label-clean">Device cost per unit</div>
              </div>
            </div>

            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Service Contract Type Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={analyticsData?.contract_distribution || []} dataKey="count" nameKey="service_contract_type" cx="50%" cy="50%" outerRadius={100} label={(entry) => `${entry.count}`}>
                      {(analyticsData?.contract_distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Device vs Contract Cost Ratio</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={[
                      { name: 'Devices', value: (analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_device_cost || 0), 0) },
                      { name: 'Contracts', value: (analyticsData?.cost_by_department || []).reduce((sum, dept) => sum + (dept.total_contract_cost || 0), 0) }
                    ]} 
                    dataKey="value" 
                    nameKey="name" 
                    cx="50%" 
                    cy="50%" 
                    outerRadius={100} 
                    // Formats the labels shown directly on the chart slices
                    label={({ value }) => formatCurrencyDynamic(value)}
                    >
                      <Cell fill="var(--dp-accent-primary)" />
                      <Cell fill="#f39c12" />
                    </Pie>
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="chart-card-clean full-width">
              <h4>Total Cost Distribution by Department</h4>
              <div className="high-value-table-container">
                <table className="high-value-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #f3f4f6 100%)', borderBottom: '2px solid #e5e7eb' }}>
                    <tr>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'left', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('department')} title="Sort by department">Department{renderSortIndicator('department', costDeptSortConfig)}</th>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('device_count')} title="Sort by device count">Devices{renderSortIndicator('device_count', costDeptSortConfig)}</th>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('device_cost')} title="Sort by device cost">Device Cost{renderSortIndicator('device_cost', costDeptSortConfig)}</th>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('contract_cost')} title="Sort by contract cost">Contract Cost{renderSortIndicator('contract_cost', costDeptSortConfig)}</th>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('total_cost')} title="Sort by total cost">Total Cost{renderSortIndicator('total_cost', costDeptSortConfig)}</th>
                      <th className="sortable" style={{ padding: '1rem', textAlign: 'center', color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem' }} onClick={() => handleCostDeptSort('percentage')} title="Sort by percent of total">% of Total{renderSortIndicator('percentage', costDeptSortConfig)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const depts = (analyticsData?.cost_by_department || [])
                        .map(dept => ({
                          department: dept.department || 'Unassigned',
                          device_count: dept.device_count,
                          device_cost: dept.total_device_cost || 0,
                          contract_cost: dept.total_contract_cost || 0,
                          total_cost: (dept.total_device_cost || 0) + (dept.total_contract_cost || 0),
                          icon: dept.icon || '🏢'
                        }));
                      const grandTotal = depts.reduce((sum, d) => sum + d.total_cost, 0);
                      const deptsWithPct = depts.map((dept) => ({
                        ...dept,
                        percentage: grandTotal > 0 ? (dept.total_cost / grandTotal) * 100 : 0
                      }));
                      const sortedDepts = getSortedData(deptsWithPct, costDeptSortConfig);
                      return sortedDepts.map((dept, index) => {
                        const percentage = dept.percentage.toFixed(1);
                        const barWidth = dept.percentage;
                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '1rem', fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '1.2rem' }}>{dept.icon}</span>
                              <span>{dept.department}</span>
                            </td>
                            <td style={{ padding: '1rem', textAlign: 'center', color: 'var(--dp-accent-primary)', fontWeight: 700 }}>{dept.device_count}</td>
                            <td style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontFamily: 'Open Sans', fontSize: '0.85rem', fontWeight: 700 }}>₹{dept.device_cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontFamily: 'Open Sans', fontSize: '0.85rem', fontWeight: 700 }}>₹{dept.contract_cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '1rem', textAlign: 'right', color: '#2c3e50', fontWeight: 700, fontFamily: "'Open Sans'" }}>₹{dept.total_cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                                <div style={{ width: '60px', height: '24px', background: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                                  <div style={{ width: `${barWidth}%`, height: '100%', background: `var(--dp-accent-gradient)` }}></div>
                                </div>
                                <span style={{ fontWeight: 700, color: 'var(--dp-accent-primary)', fontSize: '0.85rem' }}>{percentage}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
      </div>
      )}

      {/* SECTION 5: COMPLAINTS & ISSUES */}
      <div className="analytics-section-wrapper" id="section-complaints">
        <SectionHeader icon="🚨" title="Complaints & Issues" sectionKey="complaints" />
        <div className="section-content">
            <AnalyticsContextBanner variant="status" title="What counts as active">
              Active counts include <strong>open</strong>, <strong>acknowledged</strong>, and <strong>in progress</strong> complaints. Severity cards show active issues; totals include all statuses.
            </AnalyticsContextBanner>
            <div className="key-insights-grid">
              <div className="insight-card-clean critical">
                <div className="card-header-clean"><span className="card-icon-clean">🔴</span><h3>Critical Issues</h3></div>
                <div className="card-value-clean">{analyticsData?.complaints_severity?.critical || 0}</div>
                <div className="card-label-clean">Open / Ack / In progress</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', fontWeight: '500' }}>
                  Total: {analyticsData?.complaints_severity?.critical_total || 0}
                </div>
              </div>
              <div className="insight-card-clean warning">
                <div className="card-header-clean"><span className="card-icon-clean">⚠️</span><h3>Major Issues</h3></div>
                <div className="card-value-clean">{analyticsData?.complaints_severity?.major || 0}</div>
                <div className="card-label-clean">Open / Ack / In progress</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', fontWeight: '500' }}>
                  Total: {analyticsData?.complaints_severity?.major_total || 0}
                </div>
              </div>
              <div className="insight-card-clean info">
                <div className="card-header-clean"><span className="card-icon-clean">ℹ️</span><h3>Minor Issues</h3></div>
                <div className="card-value-clean">{analyticsData?.complaints_severity?.minor || 0}</div>
                <div className="card-label-clean">Open / Ack / In progress</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', fontWeight: '500' }}>
                  Total: {analyticsData?.complaints_severity?.minor_total || 0}
                </div>
              </div>
              <div className="insight-card-clean success">
                <div className="card-header-clean"><span className="card-icon-clean">✅</span><h3>Active Complaints</h3></div>
                <div className="card-value-clean">{analyticsData?.active_complaints || 0}</div>
                <div className="card-label-clean">Open / Ack / In progress</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem', fontWeight: '500' }}>
                  Total: {analyticsData?.total_complaints || 0}
                </div>
              </div>
            </div>

            {/* Complaint performance — ack/closure TAT + engine-aligned MTTR/MTBF */}
            <AnalyticsContextBanner variant="metrics" title="Performance metrics window">
              Metrics below use <strong>{analyticsData?.complaint_metrics?.observation_window?.label || 'Last 12 months'}</strong>. Acknowledgement and closure TAT are report → ack and report → close; restore time and MTBF match Device View (impacting, breakdown-based).
            </AnalyticsContextBanner>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1.5rem',
              marginTop: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: '6px', border: '1px solid var(--dp-accent-focus-ring)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '500' }}>Acknowledgement TAT</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: 'var(--dp-accent-primary)', marginBottom: '0.25rem' }}>
                  {formatFleetMetricDays(analyticsData?.complaint_metrics?.ack_tat)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {analyticsData?.complaint_metrics?.ack_tat?.count || 0} acknowledged
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Report to acknowledge
                </div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: '6px', border: '1px solid var(--dp-accent-focus-ring)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '500' }}>Closure TAT</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: 'var(--dp-accent-primary)', marginBottom: '0.25rem' }}>
                  {formatFleetMetricDays(analyticsData?.complaint_metrics?.closure_tat)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {analyticsData?.complaint_metrics?.closure_tat?.count || 0} closed
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Report to close
                </div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: '6px', border: '1px solid var(--dp-accent-focus-ring)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '500' }}>Avg restore (12 mo)</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: 'var(--dp-accent-primary)', marginBottom: '0.25rem' }}>
                  {formatFleetMetricDays(analyticsData?.complaint_metrics?.mttr)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {analyticsData?.complaint_metrics?.mttr?.count || 0} impacting closures
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Breakdown to close (MTTR)
                </div>
              </div>
              <div style={{ padding: '1rem', backgroundColor: '#f0f4ff', borderRadius: '6px', border: '1px solid var(--dp-accent-focus-ring)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '500' }}>MTBF (12 mo)</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: 'var(--dp-accent-primary)', marginBottom: '0.25rem' }}>
                  {formatFleetMetricDays(analyticsData?.complaint_metrics?.mtbf)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                  {analyticsData?.complaint_metrics?.mtbf?.count || 0} devices
                </div>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Between impacting failures
                </div>
              </div>
            </div>
            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Complaint Performance Metrics Trends (Last 6 Months)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData?.complaint_metrics_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      formatter={(value) => value?.toFixed(2) + ' days'}
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="ack_tat_days"
                      stroke="var(--dp-accent-primary)"
                      strokeWidth={2}
                      name="Acknowledgement TAT"
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="closure_tat_days"
                      stroke="#6366f1"
                      strokeWidth={2}
                      name="Closure TAT"
                      dot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="mttr_days"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Avg restore (MTTR)"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>MTBF Trend (Last 6 Months)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData?.mtbf_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis label={{ value: 'Days Between Failures', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      formatter={(value) => value?.toFixed(2) + ' days'}
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="mtbf_days"
                      stroke="#f59e0b"
                      strokeWidth={3}
                      name="MTBF (Mean Time Between Failures)"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="charts-grid-clean">
              <div className="chart-card-clean">
                <h4>Complaint Trends (Last 6 Months)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analyticsData?.complaint_trends || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#f57c00" strokeWidth={3} name="Complaints Reported" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Complaint Status Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={(analyticsData?.complaints_by_status || []).map(item => ({
                      ...item,
                      displayName: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown'
                    }))} dataKey="count" nameKey="displayName" cx="50%" cy="45%" outerRadius={90} label>
                      <Cell fill="#f57c00" />
                      <Cell fill="#1976d2" />
                      <Cell fill="#28a745" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Complaint Severity Distribution (Open / Ack / In progress)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={[
                      { name: 'Critical', value: analyticsData?.complaints_severity?.critical || 0 },
                      { name: 'Major', value: analyticsData?.complaints_severity?.major || 0 },
                      { name: 'Minor', value: analyticsData?.complaints_severity?.minor || 0 }
                    ]} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(entry) => `${entry.value}`}>
                      <Cell fill="#e74c3c" />
                      <Cell fill="#f39c12" />
                      <Cell fill="#3498db" />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-card-clean">
                <h4>Active Complaints by Category</h4>
                {(() => {
                  const categoryData = (analyticsData?.complaints_by_category || []).map((item) => ({
                    name: item.label || 'Unknown',
                    count: item.count || 0,
                  }));
                  const chartHeight = Math.max(300, 48 + categoryData.length * 36);
                  return categoryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <BarChart
                        data={categoryData}
                        layout="vertical"
                        margin={{ top: 8, right: 36, left: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={150}
                          tick={{ fontSize: 12, fill: '#4b5563' }}
                          interval={0}
                        />
                        <Tooltip
                          contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }}
                        />
                        <Bar dataKey="count" name="Active complaints" radius={[0, 8, 8, 0]}>
                          {categoryData.map((_, index) => (
                            <Cell key={`complaint-category-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                          <LabelList
                            dataKey="count"
                            position="right"
                            style={{ fill: '#374151', fontSize: 12, fontWeight: 600 }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="no-data">No active complaints by category</div>
                  );
                })()}
              </div>
            </div>
        </div>
      </div>

      {/* SECTION 6: DEPARTMENT PERFORMANCE */}
      <div className="analytics-section-wrapper" id="section-departmentPerformance">
        <SectionHeader icon="🏥" title="Department Performance" sectionKey="departmentPerformance" />
        {deviceAvailability && deviceAvailability.length > 0 && analyticsData?.device_risk_scores && (
        <div className="section-content">
            <div className="availability-table-section">
              <AnalyticsContextBanner variant="window" title="12-month availability window">
                Availability percentages use a rolling <strong>last 12 months</strong> window per device (per-device reliability engine).
              </AnalyticsContextBanner>
              <h4>Device Availability Metrics by Department <span className="analytics-window-badge">12 mo</span></h4>
              <div className="availability-table-container">
                <table className="availability-table compact">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleAvailDeptSort('department')} title="Sort by department">
                        <span className="th-sort-row">Department{renderSortIndicator('department', availDeptSortConfig)}</span>
                      </th>
                      <th className="center sortable" onClick={() => handleAvailDeptSort('deviceCount')} title="Sort by device count">
                        <span className="th-sort-row">Device Count{renderSortIndicator('deviceCount', availDeptSortConfig)}</span>
                      </th>
                      <th className="center sortable" onClick={() => handleAvailDeptSort('avgAvailability')} title="Sort by average availability">
                        <span className="th-stack">
                          <span className="th-sort-row">Avg Availability %{renderSortIndicator('avgAvailability', availDeptSortConfig)}</span>
                          <span className="th-stack-sub">(12 mo)</span>
                        </span>
                      </th>
                      <th className="center sortable" onClick={() => handleAvailDeptSort('highAvailability')} title="Sort by high availability count">
                        <span className="th-stack">
                          <span className="th-sort-row">High Availability{renderSortIndicator('highAvailability', availDeptSortConfig)}</span>
                          <span className="th-stack-sub">(≥95%)</span>
                        </span>
                      </th>
                      <th className="center sortable" onClick={() => handleAvailDeptSort('lowAvailability')} title="Sort by low availability count">
                        <span className="th-stack">
                          <span className="th-sort-row">Low Availability{renderSortIndicator('lowAvailability', availDeptSortConfig)}</span>
                          <span className="th-stack-sub">(&lt;80%)</span>
                        </span>
                      </th>
                      <th className="center sortable" onClick={() => handleAvailDeptSort('activeComplaints')} title="Sort by active complaints">
                        <span className="th-sort-row">Active Complaints{renderSortIndicator('activeComplaints', availDeptSortConfig)}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Create a map of device IDs to active complaints from analytics data
                      const deviceComplaintsMap = {};
                      analyticsData.device_risk_scores.forEach(device => {
                        deviceComplaintsMap[device.id] = device.active_complaints || 0;
                      });

                      const departmentStats = {};
                      deviceAvailability.forEach(device => {
                        const dept = device.department || 'Unassigned';
                        if (!departmentStats[dept]) {
                          departmentStats[dept] = { devices: [], activeComplaints: 0, icon: device.department_icon };
                        }
                        departmentStats[dept].devices.push(device);
                        // Use the active complaints from analytics data
                        departmentStats[dept].activeComplaints += deviceComplaintsMap[device.id] || 0;
                      });
                      const deptMetrics = Object.entries(departmentStats).map(([dept, stats]) => {
                        const avgAvail = stats.devices.reduce((sum, d) => sum + d.availability_percentage, 0) / stats.devices.length;
                        const highAvail = stats.devices.filter(d => d.availability_percentage >= 95).length;
                        const lowAvail = stats.devices.filter(d => d.availability_percentage < 80).length;
                        return { department: dept, deviceCount: stats.devices.length, avgAvailability: avgAvail, highAvailability: highAvail, lowAvailability: lowAvail, activeComplaints: stats.activeComplaints, devices: stats.devices, icon: stats.icon };
                      });
                      return getSortedData(deptMetrics, availDeptSortConfig).map((dept, index) => {
                        const availColor = dept.avgAvailability >= 95 ? '#27ae60' : dept.avgAvailability >= 80 ? '#f39c12' : '#e74c3c';
                        return (
                          <tr key={index} className="clickable-row" onClick={() => setSelectedDepartment({ department: dept.department, devices: dept.devices, metrics: dept, icon: dept.icon || '🏢' })}>
                            <td className="device-name-col">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <span style={{ fontSize: '1.2rem' }}>{dept.icon || '🏢'}</span>
                                <span>{dept.department}</span>
                              </div>
                            </td>
                            <td className="center">{dept.deviceCount}</td>
                            <td className="center"><span style={{ color: availColor, fontWeight: 700 }}>{dept.avgAvailability.toFixed(1)}%</span></td>
                            <td className="center"><span style={{ color: '#27ae60', fontWeight: 600 }}>{dept.highAvailability}</span></td>
                            <td className="center"><span style={{ color: '#e74c3c', fontWeight: 600 }}>{dept.lowAvailability}</span></td>
                            <td className="center">
                              <span className={`complaint-badge${dept.activeComplaints === 0 ? ' zero' : ''}`}>
                                {dept.activeComplaints}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
      )}
      </div>

      {/* SECTION 7: HIGH-VALUE ASSETS */}
      {canSeeFinancialData && (
      <div className="analytics-section-wrapper" id="section-highValueAssets">
        <SectionHeader icon="💎" title="High-Value Assets" sectionKey="highValueAssets" />
        {analyticsData?.high_value_equipment && analyticsData.high_value_equipment.length > 0 && (
        <div className="section-content">
            <div style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#666' }}>
              <strong>Top {analyticsData.high_value_equipment.length} Assets</strong> out of <strong>{analyticsData.total_devices}</strong> total devices
            </div>
            <div className="high-value-table-container">
              <table className="high-value-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleHighValueSort('name')} title="Sort by device name">Device Name{renderSortIndicator('name', highValueSortConfig)}</th>
                    <th className="sortable" onClick={() => handleHighValueSort('device_identifier')} title="Sort by device ID">Device ID{renderSortIndicator('device_identifier', highValueSortConfig)}</th>
                    <th className="sortable" onClick={() => handleHighValueSort('department')} title="Sort by department">Department{renderSortIndicator('department', highValueSortConfig)}</th>
                    <th className="sortable" onClick={() => handleHighValueSort('product_cost')} title="Sort by device cost">Device Cost{renderSortIndicator('product_cost', highValueSortConfig)}</th>
                    <th className="sortable" onClick={() => handleHighValueSort('service_contract_cost')} title="Sort by annual contract">Annual Contract{renderSortIndicator('service_contract_cost', highValueSortConfig)}</th>
                    <th className="sortable" onClick={() => handleHighValueSort('warranty_status')} title="Sort by warranty status">Warranty Status{renderSortIndicator('warranty_status', highValueSortConfig)}</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedData(
                    (analyticsData.high_value_equipment || []).map((device) => ({
                      ...device,
                      product_cost: device.product_cost || 0,
                      service_contract_cost: device.service_contract_cost || 0,
                      device_identifier: device.device_identifier || '',
                      department: device.department || '',
                      warranty_status: device.warranty_expiry
                        ? (new Date(device.warranty_expiry) > new Date() ? 'Active' : 'Expired')
                        : 'N/A'
                    })),
                    highValueSortConfig
                  ).map((device, index) => (
                    <tr key={index}>
                      <td className="device-name-col">{device.name}</td>
                      <td><DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" /></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>{device.department_icon || '🏢'}</span>
                          <span className="dept-badge">{device.department}</span>
                        </div>
                      </td>
                      <td className="cost-col">₹{(device.product_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="cost-col">₹{(device.service_contract_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>{device.warranty_status === 'Active' ? <span className="status-active">✓ Active</span> : device.warranty_status === 'Expired' ? <span className="status-expired">✗ Expired</span> : <span className="status-na">N/A</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Department availability modal — transfer-style shell (scoped; avoids shared .modal-content padding) */}
      {selectedDepartment && ReactDOM.createPortal(
        <div
          className="dept-avail-modal-overlay"
          onClick={() => setSelectedDepartment(null)}
        >
          <div
            className="dept-avail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dept-avail-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dept-avail-modal-header">
              <h2 id="dept-avail-modal-title">
                <span className="dept-avail-modal-title-icon" aria-hidden="true">
                  {selectedDepartment.icon || selectedDepartment.metrics?.icon || '🏢'}
                </span>
                {selectedDepartment.department}
              </h2>
              <ModalCloseButton
                onClick={() => setSelectedDepartment(null)}
                title="Close"
              />
            </div>

            <div className="dept-avail-modal-body">
              <div className="dept-avail-metrics" role="group" aria-label="Department summary">
                <div className="dept-avail-metric">
                  <span className="dept-avail-metric-label">Devices</span>
                  <span className="dept-avail-metric-value">{selectedDepartment.metrics.deviceCount}</span>
                </div>
                <div className="dept-avail-metric">
                  <span className="dept-avail-metric-label">Avg Availability (12 mo)</span>
                  <span className="dept-avail-metric-value">{selectedDepartment.metrics.avgAvailability.toFixed(1)}%</span>
                </div>
                <div className="dept-avail-metric">
                  <span className="dept-avail-metric-label">Active Complaints</span>
                  <span className="dept-avail-metric-value">{selectedDepartment.metrics.activeComplaints}</span>
                </div>
              </div>

              <div className="dept-avail-tabs" role="tablist" aria-label="Department views">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!selectedDepartment.showDistribution}
                  className={`dept-avail-tab${!selectedDepartment.showDistribution ? ' active' : ''}`}
                  onClick={() => setSelectedDepartment({ ...selectedDepartment, showDistribution: false })}
                >
                  Device List
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!!selectedDepartment.showDistribution}
                  className={`dept-avail-tab${selectedDepartment.showDistribution ? ' active' : ''}`}
                  onClick={() => setSelectedDepartment({ ...selectedDepartment, showDistribution: true })}
                >
                  Availability Distribution
                </button>
              </div>

              {!selectedDepartment.showDistribution ? (
                <div className="dept-avail-table-wrap">
                  <table className="comprehensive-modal-table">
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th className="center">
                          <span className="th-stack">
                            <span className="th-stack-main">Availability %</span>
                            <span className="th-stack-sub">(12 mo)</span>
                          </span>
                        </th>
                        <th className="center">Status</th>
                        <th className="center">Complaints</th>
                        <th className="center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDepartment.devices.map((device) => {
                        const availColor = device.availability_percentage >= 95 ? '#16a34a' : device.availability_percentage >= 80 ? '#d97706' : '#dc2626';
                        const availTier = device.availability_percentage >= 95 ? 'excellent' : device.availability_percentage >= 80 ? 'good' : 'poor';
                        const availLabel = device.availability_percentage >= 95 ? 'Excellent' : device.availability_percentage >= 80 ? 'Good' : 'Poor';
                        const deviceRiskData = analyticsData?.device_risk_scores?.find(d => d.id === device.id);
                        const activeComplaints = deviceRiskData?.active_complaints || 0;
                        return (
                          <tr key={device.id}>
                            <td className="device-name-col" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                              <span style={{ fontWeight: 600, color: '#2c3e50' }}>{device.name}</span>
                              <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                            </td>
                            <td className="center"><span style={{ color: availColor, fontWeight: 700 }}>{device.availability_percentage.toFixed(1)}%</span></td>
                            <td className="center">
                              <span
                                className={`availability-tier availability-tier--${availTier}`}
                                title={`Availability ${device.availability_percentage.toFixed(1)}% (12 mo)`}
                              >
                                {availLabel}
                              </span>
                            </td>
                            <td className="center"><span className="complaint-badge">{activeComplaints}</span></td>
                            <td className="center">
                              <div className="action-buttons-container">
                                <button
                                  className="action-icon-btn action-device-icon"
                                  onClick={() => onNavigateToDeviceView && onNavigateToDeviceView(device.id)}
                                  title="View device details"
                                >
                                  👁️
                                </button>
                                <button
                                  className="action-icon-btn action-complaint-icon"
                                  onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ deviceIds: [device.device_identifier] })}
                                  title="View complaints for this device"
                                >
                                  🔧
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="dept-avail-distribution" role="tabpanel">
                  <div className="dept-avail-distribution-grid">
                    <div className="dept-avail-dist-card dept-avail-dist-card--excellent">
                      <div className="dept-avail-dist-icon" aria-hidden="true">✅</div>
                      <div className="dept-avail-dist-label">Excellent</div>
                      <div className="dept-avail-dist-value">
                        {selectedDepartment.devices.filter(d => d.availability_percentage >= 95).length}
                      </div>
                      <div className="dept-avail-dist-range">≥95%</div>
                    </div>
                    <div className="dept-avail-dist-card dept-avail-dist-card--good">
                      <div className="dept-avail-dist-icon" aria-hidden="true">⚠️</div>
                      <div className="dept-avail-dist-label">Good</div>
                      <div className="dept-avail-dist-value">
                        {selectedDepartment.devices.filter(d => d.availability_percentage >= 80 && d.availability_percentage < 95).length}
                      </div>
                      <div className="dept-avail-dist-range">80–94%</div>
                    </div>
                    <div className="dept-avail-dist-card dept-avail-dist-card--poor">
                      <div className="dept-avail-dist-icon" aria-hidden="true">❌</div>
                      <div className="dept-avail-dist-label">Poor</div>
                      <div className="dept-avail-dist-value">
                        {selectedDepartment.devices.filter(d => d.availability_percentage < 80).length}
                      </div>
                      <div className="dept-avail-dist-range">&lt;80%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Risk Score Info Modal - Reusable Component */}
      <RiskScoreInfoModal isOpen={showRiskScoreInfo} onClose={() => setShowRiskScoreInfo(false)} />
    </div>
  );
}

export default Analytics;
